// Lightweight haptics wrapper. Uses Capacitor Haptics on native, falls back
// to navigator.vibrate on web (where supported). Respects prefers-reduced-motion.
import { useCallback } from 'react';

const isReducedMotion = () => {
  try {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { return false; }
};

let HapticsModule = null;
let triedLoad = false;
const loadHaptics = async () => {
  if (triedLoad) return HapticsModule;
  triedLoad = true;
  try {
    const native = await import('../lib/native');
    if (!native.isNative()) return null;
    const mod = await import('@capacitor/haptics').catch(() => null);
    if (mod) HapticsModule = mod;
  } catch (e) { /* no-op */ }
  return HapticsModule;
};

export function useHaptics() {
  const trigger = useCallback(async (kind = 'light') => {
    if (isReducedMotion()) return;
    const mod = await loadHaptics();
    if (mod && mod.Haptics) {
      try {
        if (kind === 'success') return mod.Haptics.notification({ type: 'SUCCESS' });
        if (kind === 'warning') return mod.Haptics.notification({ type: 'WARNING' });
        if (kind === 'error') return mod.Haptics.notification({ type: 'ERROR' });
        if (kind === 'medium') return mod.Haptics.impact({ style: 'MEDIUM' });
        if (kind === 'heavy') return mod.Haptics.impact({ style: 'HEAVY' });
        return mod.Haptics.impact({ style: 'LIGHT' });
      } catch (e) { /* fall through */ }
    }
    // Web fallback
    if (navigator.vibrate) {
      const pattern = { light: 6, medium: 12, heavy: 22, success: [4, 30, 8], warning: [10, 30, 10], error: [12, 40, 12, 40, 12] }[kind] || 6;
      try { navigator.vibrate(pattern); } catch (e) {}
    }
  }, []);
  return { trigger };
}

export default useHaptics;
